import React, { startTransition, useCallback, useDeferredValue, useEffect, useState } from "react";
import GradientRectangle from "./GradientRectangle";
import NumberWithUnit from "./NumberWithUnit";
import WebXREntry from "./xr/WebXREntry";
import "./styles.css";
import {
  APP_BUILD_ID,
  AVAILABLE_VELOCITY_TEXTURE_LEVELS,
  DATASET_METADATA_PATH,
  DEFAULT_WIND_LAYER_BLEND_MODE,
  DEFAULT_SURFACE_MODE,
  DEFAULT_VELOCITY_TEXTURE_LEVEL,
  WIND_LAYER_BLEND_MODES,
  buildWindLegendStops,
  buildPressureLegendStops,
  formatWindLayerBlendLabel,
  formatWindLayerShortLabel,
  getWindLayerBlendCount,
  getWindMagnitudeRangeForLevel,
  getPressureLimitsForLevel,
  SURFACE_MODES,
  clamp,
  withBasePath,
} from "./constants";
import ThreeScene from "./components/Main";
import { WEBXR_SUPPORT_STATUS, detectWebXRSupport } from "./xr/webxrSupport.mjs";

const initialTextureIndex = Math.max(
  AVAILABLE_VELOCITY_TEXTURE_LEVELS.indexOf(DEFAULT_VELOCITY_TEXTURE_LEVEL),
  0
);

function openStandaloneWhenEmbedded() {
  if (typeof window === "undefined") {
    return false;
  }

  let isEmbedded = false;

  try {
    isEmbedded = window.self !== window.top;
  } catch (error) {
    isEmbedded = true;
  }

  if (!isEmbedded) {
    return false;
  }

  try {
    window.top.location.assign(window.location.href);
  } catch (error) {
    window.open(window.location.href, "_blank", "noopener,noreferrer");
  }

  return true;
}

function formatCoordinate(value, positiveSuffix, negativeSuffix) {
  if (!Number.isFinite(value)) {
    return "--";
  }

  return `${Math.abs(value).toFixed(1)} deg ${value >= 0 ? positiveSuffix : negativeSuffix}`;
}

function formatSignedSampleValue(value, unit = "") {
  if (!Number.isFinite(value)) {
    return "--";
  }

  const sign = value > 0 ? "+" : "";
  return `${sign}${value.toFixed(1)}${unit ? ` ${unit}` : ""}`;
}

function formatRangeValue(value) {
  if (!Number.isFinite(value)) {
    return "--";
  }

  if (value >= 100) {
    return `${Math.round(value)}`;
  }

  if (value >= 10) {
    return `${Math.round(value)}`;
  }

  return `${Math.round(value * 10) / 10}`;
}

function formatWindRangeSubtitle(range) {
  return `Auto ${formatRangeValue(range?.min)}-${formatRangeValue(range?.max)} m/s`;
}

export default function App() {
  const [textureIndex, setTextureIndex] = useState(initialTextureIndex);
  const [surfaceMode, setSurfaceMode] = useState(DEFAULT_SURFACE_MODE);
  const [showWinds, setShowWinds] = useState(true);
  const [windLayerBlendMode, setWindLayerBlendMode] = useState(DEFAULT_WIND_LAYER_BLEND_MODE);
  const [isXrSurfaceOpaque, setIsXrSurfaceOpaque] = useState(true);
  const [selectedPoint, setSelectedPoint] = useState(null);
  const [datasetMetadata, setDatasetMetadata] = useState(null);
  const [measuredWindMagnitudeRange, setMeasuredWindMagnitudeRange] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [webxrSupport, setWebxrSupport] = useState({
    status: WEBXR_SUPPORT_STATUS.webxrUnavailable,
    supportedInCurrentBrowser: false,
    likelyQuestBrowser: false,
    canShowSendToQuest: false,
    questLaunchUrl: null,
  });
  const [xrHandle, setXrHandle] = useState(null);
  const [isEnteringVR, setIsEnteringVR] = useState(false);
  const [isVRPresenting, setIsVRPresenting] = useState(false);
  const [xrErrorMessage, setXrErrorMessage] = useState("");
  const [xrStatusMessage, setXrStatusMessage] = useState("Idle");

  const selectedTextureLevel =
    AVAILABLE_VELOCITY_TEXTURE_LEVELS[textureIndex] ?? DEFAULT_VELOCITY_TEXTURE_LEVEL;
  const deferredTextureLevel = useDeferredValue(selectedTextureLevel);
  const isUpdatingLayer = deferredTextureLevel !== selectedTextureLevel;
  const metadataWindMagnitudeRange = getWindMagnitudeRangeForLevel(
    datasetMetadata,
    deferredTextureLevel
  );
  const activeWindMagnitudeRange =
    measuredWindMagnitudeRange?.level === deferredTextureLevel &&
    measuredWindMagnitudeRange?.layerBlendMode === windLayerBlendMode
      ? measuredWindMagnitudeRange.range
      : metadataWindMagnitudeRange;
  const activeWindLayerCount = getWindLayerBlendCount(
    deferredTextureLevel,
    windLayerBlendMode
  );
  const activeWindMagnitudeLimit = activeWindMagnitudeRange.max;
  const activePressureLimits = getPressureLimitsForLevel(
    datasetMetadata,
    deferredTextureLevel
  );
  const windLegendStops = buildWindLegendStops(activeWindMagnitudeRange);
  const pressureLegendStops = buildPressureLegendStops(
    activePressureLimits.min,
    activePressureLimits.max
  );
  const legendEntries = [];

  if (showWinds) {
    legendEntries.push({
      key: "winds",
      title: `Wind speed (${activeWindLayerCount} layer${activeWindLayerCount === 1 ? "" : "s"})`,
      subtitle: formatWindRangeSubtitle(activeWindMagnitudeRange),
      stops: windLegendStops,
      orientation: "horizontal",
    });
  }

  if (surfaceMode === SURFACE_MODES.pressure) {
    legendEntries.push({
      key: "pressure",
      title: "Pressure surface",
      subtitle: "kPa",
      stops: pressureLegendStops,
      orientation: "horizontal",
    });
  }

  const statusMessage = isUpdatingLayer
    ? "Updating active layer"
    : showWinds
      ? `Climate station live / ${formatWindLayerBlendLabel(windLayerBlendMode)}`
      : "Surface view active";
  const sampleSwatchColor = selectedPoint?.colour ?? windLegendStops[0].color;

  const handleTextureLevelStep = useCallback((step) => {
    startTransition(() => {
      setTextureIndex((currentIndex) => clamp(
        currentIndex + step,
        0,
        AVAILABLE_VELOCITY_TEXTURE_LEVELS.length - 1
      ));
    });
  }, []);

  const handleWindMagnitudeRangeChange = useCallback(({ level, layerBlendMode, range }) => {
    if (!Number.isFinite(level) || !range) {
      return;
    }

    setMeasuredWindMagnitudeRange({
      level,
      layerBlendMode: layerBlendMode ?? windLayerBlendMode,
      range,
    });
  }, [windLayerBlendMode]);

  const handleEnterVR = useCallback(async () => {
    if (!xrHandle?.enterVR) {
      return;
    }

    setXrErrorMessage("");
    setXrStatusMessage("Starting VR request");

    if (openStandaloneWhenEmbedded()) {
      setXrErrorMessage("Opening the standalone VR page. Select Enter VR again after it loads.");
      setXrStatusMessage("Opening standalone page");
      return;
    }

    setIsEnteringVR(true);

    try {
      await xrHandle.enterVR();
    } catch (error) {
      setXrErrorMessage(error?.message ?? "Unable to start VR mode.");
      setXrStatusMessage("VR request failed");
    } finally {
      setIsEnteringVR(false);
    }
  }, [xrHandle]);

  useEffect(() => {
    let cancelled = false;

    async function loadWindMetadata() {
      try {
        const response = await fetch(DATASET_METADATA_PATH);
        if (!response.ok) {
          throw new Error(`Metadata request failed with ${response.status}`);
        }

        const metadata = await response.json();
        if (!cancelled) {
          setDatasetMetadata(metadata);
        }
      } catch (error) {
        console.warn("Unable to load wind metadata, using default legend scale.", error);
      }
    }

    loadWindMetadata();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    detectWebXRSupport().then((result) => {
      if (!cancelled) {
        setWebxrSupport(result);
      }
    });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setSelectedPoint(null);
  }, [selectedTextureLevel, windLayerBlendMode]);

  useEffect(() => {
    if (isVRPresenting) {
      setIsMobileMenuOpen(false);
    }
  }, [isVRPresenting]);

  useEffect(() => {
    if (!isMobileMenuOpen) {
      return undefined;
    }

    function handleKeyDown(event) {
      if (event.key === "Escape") {
        setIsMobileMenuOpen(false);
      }
    }

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isMobileMenuOpen]);

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return undefined;
    }

    const desktopBreakpoint = window.matchMedia("(min-width: 721px)");
    const handleViewportChange = (event) => {
      if (event.matches) {
        setIsMobileMenuOpen(false);
      }
    };

    handleViewportChange(desktopBreakpoint);

    if (typeof desktopBreakpoint.addEventListener === "function") {
      desktopBreakpoint.addEventListener("change", handleViewportChange);
      return () => {
        desktopBreakpoint.removeEventListener("change", handleViewportChange);
      };
    }

    desktopBreakpoint.addListener(handleViewportChange);
    return () => {
      desktopBreakpoint.removeListener(handleViewportChange);
    };
  }, []);

  return (
    <main className={`app-shell ${isVRPresenting ? "app-shell--xr-active" : ""}`}>
      <div className="scene-layer">
        <ThreeScene
          textureLevel={deferredTextureLevel}
          surfaceMode={surfaceMode}
          showWinds={showWinds}
          windMagnitudeLimit={activeWindMagnitudeLimit}
          windMagnitudeRange={activeWindMagnitudeRange}
          windLayerBlendMode={windLayerBlendMode}
          legendEntries={legendEntries}
          selectedPoint={selectedPoint}
          isXrSurfaceOpaque={isXrSurfaceOpaque}
          onPointSelected={setSelectedPoint}
          onTextureLevelStep={handleTextureLevelStep}
          onSurfaceModeChange={setSurfaceMode}
          onShowWindsChange={setShowWinds}
          onXrSurfaceOpaqueChange={setIsXrSurfaceOpaque}
          onWindLayerBlendModeChange={setWindLayerBlendMode}
          onWindMagnitudeRangeChange={handleWindMagnitudeRangeChange}
          onVRReady={setXrHandle}
          onVRPresentingChange={setIsVRPresenting}
          onVRStatusChange={setXrStatusMessage}
        />
      </div>

      <button
        type="button"
        className={`app-menu-toggle ${isMobileMenuOpen ? "is-open" : ""}`}
        onClick={() => setIsMobileMenuOpen((current) => !current)}
        aria-controls="app-overlay"
        aria-expanded={isMobileMenuOpen}
        aria-label={isMobileMenuOpen ? "Close controls menu" : "Open controls menu"}
      >
        <span className="app-menu-toggle__icon" aria-hidden="true">
          <span />
          <span />
          <span />
        </span>
        <span className="app-menu-toggle__label">
          {isMobileMenuOpen ? "Close" : "Menu"}
        </span>
      </button>

      <button
        type="button"
        className={`app-overlay-backdrop ${isMobileMenuOpen ? "is-visible" : ""}`}
        onClick={() => setIsMobileMenuOpen(false)}
        aria-label="Close controls menu"
        tabIndex={isMobileMenuOpen ? 0 : -1}
      />

      <div
        id="app-overlay"
        className={`app-overlay ${isMobileMenuOpen ? "app-overlay--open" : ""}`}
      >
        <section className="panel hero-panel">
          <p className="eyebrow">OASIS Virtual Climate Station</p>
          <h1>Jupiter Station</h1>
          <p className="lead">
            Research-grade atmospheric flow visualisation with layer-stacked tracers,
            wind probes, and an immersive VR station interface.
          </p>
          <p className="hero-note">
            This prototype offers an early preview of the platform. Additional features,
            datasets, and supporting information will be introduced soon.
          </p>
          <p className="hero-credit">Created by Stanley Witt (C) 2026</p>
          <div className="status-row">
            <span className={`status-dot ${isUpdatingLayer ? "status-dot--busy" : ""}`} />
            <span>{statusMessage}</span>
          </div>
        </section>

        <section className="panel controls-panel">
          <div className="panel-heading">
            <span className="panel-label">Visual Controls</span>
            <span className="panel-chip">Interactive</span>
          </div>

          <div className="control-group">
            <div className="slider-header">
              <div>
                <div className="slider-label">Surface shading</div>
                <div className="slider-note">
                  Replace the neutral globe with a pressure map from the selected layer.
                </div>
              </div>
            </div>

            <div className="mode-toggle">
              <button
                type="button"
                className={`mode-toggle__button ${surfaceMode === SURFACE_MODES.constant ? "is-active" : ""}`}
                onClick={() => setSurfaceMode(SURFACE_MODES.constant)}
              >
                Constant
              </button>
              <button
                type="button"
                className={`mode-toggle__button ${surfaceMode === SURFACE_MODES.pressure ? "is-active" : ""}`}
                onClick={() => setSurfaceMode(SURFACE_MODES.pressure)}
              >
                Pressure
              </button>
            </div>
          </div>

          <div className="control-group">
            <div className="slider-header">
              <div>
                <div className="slider-label">Tracer visibility</div>
                <div className="slider-note">
                  Show or hide the wind particles without changing the active data layer.
                </div>
              </div>
            </div>

            <div className="mode-toggle">
              <button
                type="button"
                className={`mode-toggle__button ${showWinds ? "is-active" : ""}`}
                onClick={() => setShowWinds(true)}
              >
                Winds on
              </button>
              <button
                type="button"
                className={`mode-toggle__button ${!showWinds ? "is-active" : ""}`}
                onClick={() => setShowWinds(false)}
              >
                Winds off
              </button>
            </div>
          </div>

          <div className="control-group">
            <div className="slider-header">
              <div>
                <div className="slider-label">Altitude layer</div>
                <div className="slider-note">
                  Winds, pressure surface maps, and point sampling all use this same dataset layer.
                </div>
              </div>
              <div className="slider-value">L{selectedTextureLevel}</div>
            </div>

            <input
              className="layer-slider"
              type="range"
              min="0"
              max={AVAILABLE_VELOCITY_TEXTURE_LEVELS.length - 1}
              step="1"
              value={textureIndex}
              onChange={(event) => {
                const nextIndex = Number.parseInt(event.target.value, 10);
                startTransition(() => setTextureIndex(nextIndex));
              }}
            />
          </div>

          <div className="control-group">
            <div className="slider-header">
              <div>
                <div className="slider-label">Layer combination</div>
                <div className="slider-note">
                  Stack neighbouring altitude layers as separate tracer shells.
                </div>
              </div>
            </div>

            <div className="mode-toggle">
              <button
                type="button"
                className={`mode-toggle__button ${windLayerBlendMode === WIND_LAYER_BLEND_MODES.single ? "is-active" : ""}`}
                onClick={() => setWindLayerBlendMode(WIND_LAYER_BLEND_MODES.single)}
              >
                {formatWindLayerShortLabel(WIND_LAYER_BLEND_MODES.single)}
              </button>
              <button
                type="button"
                className={`mode-toggle__button ${windLayerBlendMode === WIND_LAYER_BLEND_MODES.adjacent ? "is-active" : ""}`}
                onClick={() => setWindLayerBlendMode(WIND_LAYER_BLEND_MODES.adjacent)}
              >
                {formatWindLayerShortLabel(WIND_LAYER_BLEND_MODES.adjacent)}
              </button>
              <button
                type="button"
                className={`mode-toggle__button ${windLayerBlendMode === WIND_LAYER_BLEND_MODES.wide ? "is-active" : ""}`}
                onClick={() => setWindLayerBlendMode(WIND_LAYER_BLEND_MODES.wide)}
              >
                {formatWindLayerShortLabel(WIND_LAYER_BLEND_MODES.wide)}
              </button>
              <button
                type="button"
                className={`mode-toggle__button ${windLayerBlendMode === WIND_LAYER_BLEND_MODES.seven ? "is-active" : ""}`}
                onClick={() => setWindLayerBlendMode(WIND_LAYER_BLEND_MODES.seven)}
              >
                {formatWindLayerShortLabel(WIND_LAYER_BLEND_MODES.seven)}
              </button>
            </div>
          </div>
        </section>

        <section className="panel sample-panel">
          <div className="panel-heading">
            <span className="panel-label">Atmospheric Probe</span>
            <span className="panel-chip">Point readout</span>
          </div>

          <NumberWithUnit
            label="Wind speed"
            value={selectedPoint ? selectedPoint.mag : "Click the globe"}
            unit={selectedPoint ? "m/s" : ""}
          />

          <div className="sample-caption">
            <span
              className="sample-swatch"
              style={{ backgroundColor: sampleSwatchColor }}
            />
            <span>
              Click any visible point on the globe to inspect local wind magnitude at the current altitude.
            </span>
          </div>

          {selectedPoint ? (
            <div className="sample-details">
              <span>Lat {formatCoordinate(selectedPoint.latitude, "N", "S")}</span>
              <span>Lon {formatCoordinate(selectedPoint.longitude, "E", "W")}</span>
              <span>Zonal {formatSignedSampleValue(selectedPoint.zonalWind, "m/s")}</span>
              <span>Meridional {formatSignedSampleValue(selectedPoint.meridionalWind, "m/s")}</span>
            </div>
          ) : null}
        </section>

        <aside className="panel legend-panel">
          <div className="legend-stack">
            {legendEntries.length ? legendEntries.map((legend) => (
              <GradientRectangle
                key={legend.key}
                title={legend.title}
                subtitle={legend.subtitle}
                stops={legend.stops}
                orientation={legend.orientation}
              />
            )) : (
              <div className="empty-state">
                Enable wind tracers or switch to the pressure surface to show an active legend.
              </div>
            )}
          </div>
        </aside>

        <WebXREntry
          support={webxrSupport}
          isReady={Boolean(xrHandle?.enterVR)}
          isEntering={isEnteringVR}
          isPresenting={isVRPresenting}
          errorMessage={xrErrorMessage}
          statusMessage={xrStatusMessage}
          buildId={APP_BUILD_ID}
          onEnterVR={handleEnterVR}
        />
      </div>

      <a
        className="app-brandmark"
        href="https://software-oasis.com"
        target="_blank"
        rel="noreferrer"
        aria-label="Visit the OASIS research group website"
      >
        <img
          className="app-brandmark__image"
          src={withBasePath("/branding/oasis-logo-team.webp")}
          alt="OASIS research group logo"
        />
      </a>
    </main>
  );
}
